import { isAxiosError } from 'axios';
import { appText } from 'utils/appLocale';
import { translatableErrorText } from 'utils/translatableError';
import {
  isFiniteNumber,
  isRecord,
  isString,
  isStringArray,
} from 'utils/typeGuards';

import { getApiErrorStatus, getResponseRecord } from './apiErrorResponse';

export { getApiErrorStatus } from './apiErrorResponse';

/**
 * Pull `response.data.detail` off an axios error (or a response-shaped throw).
 * Returns `unknown` because `detail` is a string on most endpoints but an object
 * on some — narrow at the call site. For display text use `getApiErrorText`,
 * which translates coded details.
 */
export function getRawApiErrorDetail(error: unknown): unknown {
  return getApiErrorBody(error)?.detail;
}

/** The response body (`response.data`) when it is an object. */
function getApiErrorBody(error: unknown): Record<string, unknown> | undefined {
  const data: unknown = isAxiosError(error)
    ? error.response?.data
    : getResponseRecord(error)?.data;
  return isRecord(data) ? data : undefined;
}

/**
 * Display text for a coded error response body (`{ detail, detailCode,
 * detailParams }`), or null for any other body. Registered lazily by the
 * recruiter app (recruiter-i18n/apiErrorText.ts), which owns the parsing.
 */
export type ApiErrorTranslator = (
  body: Record<string, unknown>
) => string | null;

let apiErrorTranslator: ApiErrorTranslator | null = null;

/**
 * Set by recruiter-i18n/index.ts (recruiter and internal apps) on the first
 * switch away from English so coded error details show in the display
 * language. The candidate app never registers one, so it keeps the server's
 * English `detail`.
 */
export const registerApiErrorTranslator = (
  fn: ApiErrorTranslator | null
): void => {
  apiErrorTranslator = fn;
};

/**
 * Client error display text, or a coded server detail in the display language
 * (when the app registered a translator), otherwise `formatApiErrorDetail` of
 * the raw `detail`. Null when there is nothing usable.
 */
export function getApiErrorText(error: unknown): string | null {
  const translated = translatableErrorText(error);
  if (translated) return translated;
  const body = getApiErrorBody(error);
  return (
    (apiErrorTranslator && body ? apiErrorTranslator(body) : null) ||
    formatApiErrorDetail(body?.detail)
  );
}

/**
 * A coded error sent inside a successful response body (the server's
 * `CodedErrorDetail`, e.g. a failed row or preview): `english` is the
 * server-rendered English and `params` (snake_case) fill the placeholders of
 * the translated text.
 */
export interface CodedErrorDetailBody {
  code: string;
  params: Readonly<Record<string, string | number | readonly string[]>>;
  english: string;
}

const isCodedErrorParamValue = (value: unknown): boolean =>
  isString(value) || isFiniteNumber(value) || isStringArray(value);

/** Whether `value` is a well-formed `CodedErrorDetailBody` (e.g. from an SSE payload). */
export const isCodedErrorDetailBody = (
  value: unknown
): value is CodedErrorDetailBody =>
  isRecord(value) &&
  isString(value.code) &&
  isString(value.english) &&
  isRecord(value.params) &&
  Object.values(value.params).every(isCodedErrorParamValue);

/**
 * A `CodedErrorDetail` in the display language, through the same registered
 * translator as error responses (`errors:detail.<camelCode>`). English, an
 * unknown code, or no translator (the candidate app, or before the chunk
 * loads) give the server's English; no detail gives `fallback`.
 */
export function codedErrorDetailText<
  Fallback extends string | null | undefined,
>(
  detail: CodedErrorDetailBody | null | undefined,
  fallback: Fallback
): string | Fallback {
  if (!detail) {
    return fallback;
  }
  const body = {
    detail: detail.english,
    detailCode: detail.code,
    detailParams: detail.params,
  };
  return (
    (apiErrorTranslator ? apiErrorTranslator(body) : null) || detail.english
  );
}

/** What a 422 validation entry says is wrong, from pydantic's `type`. */
type ValidationProblem =
  | 'required'
  | 'tooShort'
  | 'tooLong'
  | 'format'
  | 'range'
  | 'choice'
  | 'invalid';

const VALIDATION_PROBLEM_BY_TYPE: Readonly<
  Partial<Record<string, ValidationProblem>>
> = {
  missing: 'required',
  string_too_short: 'tooShort',
  too_short: 'tooShort',
  string_too_long: 'tooLong',
  too_long: 'tooLong',
  string_pattern_mismatch: 'format',
  greater_than: 'range',
  greater_than_equal: 'range',
  less_than: 'range',
  less_than_equal: 'range',
  multiple_of: 'range',
  finite_number: 'range',
  enum: 'choice',
  literal_error: 'choice',
};

const validationProblem = (type: unknown): ValidationProblem => {
  if (!isString(type)) {
    return 'invalid';
  }
  const mapped = VALIDATION_PROBLEM_BY_TYPE[type];
  if (mapped) {
    return mapped;
  }
  // int_parsing, uuid_parsing, datetime_from_date_parsing, url_parsing, ...
  return type.endsWith('_parsing') ? 'format' : 'invalid';
};

// Where FastAPI says the field came from; never a field name itself.
const REQUEST_LOCATIONS = new Set([
  'body',
  'query',
  'path',
  'header',
  'cookie',
]);
const FIELD_NAME = /^[A-Za-z_][A-Za-z0-9_-]*$/;
// Pydantic appends the failing member's tag after the field when a union like
// `int | str` fails (`['body', 'x', 'int']`); those tags are never the field.
const UNION_MEMBER_TAGS = new Set([
  'int',
  'str',
  'float',
  'bool',
  'bytes',
  'none',
  'decimal',
  'uuid',
]);

/**
 * The innermost named field of a validation `loc` (`['body', 'candidates', 0,
 * 'email']` is `email`), or null when it names none (`['body']`). It is the
 * API field name, an identifier shown as is.
 */
const validationField = (loc: unknown): string | null => {
  if (!Array.isArray(loc)) {
    return null;
  }
  for (let index = loc.length - 1; index >= 0; index -= 1) {
    const segment: unknown = loc[index];
    if (
      isString(segment) &&
      FIELD_NAME.test(segment) &&
      !(index === 0 && REQUEST_LOCATIONS.has(segment)) &&
      !(index > 0 && UNION_MEMBER_TAGS.has(segment))
    ) {
      return segment;
    }
  }
  return null;
};

/**
 * One FastAPI/pydantic validation entry (`{ loc, msg, type }`) in the display
 * language. Its `msg` is pydantic's English ("Field required"), so the text
 * comes from `type` and the field name instead.
 */
const validationEntryText = (entry: unknown): string | null => {
  if (!isRecord(entry)) {
    return null;
  }
  const field = validationField(entry.loc);
  if (!field) {
    return null;
  }
  const values = { field };
  switch (validationProblem(entry.type)) {
    case 'required':
      return appText(
        'validation.request.fieldRequired',
        'Missing required field: {{field}}',
        values
      );
    case 'tooShort':
      return appText(
        'validation.request.fieldTooShort',
        'Value too short: {{field}}',
        values
      );
    case 'tooLong':
      return appText(
        'validation.request.fieldTooLong',
        'Value too long: {{field}}',
        values
      );
    case 'format':
      return appText(
        'validation.request.fieldFormat',
        'Invalid format: {{field}}',
        values
      );
    case 'range':
      return appText(
        'validation.request.fieldRange',
        'Value out of range: {{field}}',
        values
      );
    case 'choice':
      return appText(
        'validation.request.fieldChoice',
        'Value not allowed: {{field}}',
        values
      );
    case 'invalid':
      return appText(
        'validation.request.fieldInvalid',
        'Invalid value: {{field}}',
        values
      );
  }
};

/**
 * A FastAPI 422 validation array in the display language: one message per
 * named field (duplicates dropped), or a generic invalid-request message when
 * no entry names a field. Null for an empty array. Pydantic's English `msg`
 * is never shown.
 */
const validationDetailText = (detail: readonly unknown[]): string | null => {
  if (detail.length === 0) {
    return null;
  }
  const messages = [
    ...new Set(
      detail
        .map(validationEntryText)
        .filter((text): text is string => Boolean(text))
    ),
  ];
  if (messages.length === 0) {
    return appText(
      'validation.request.invalid',
      'The request contains invalid data. Check your entries and try again.'
    );
  }
  return messages.join('; ');
};

/**
 * A `detail` payload rendered as text: a plain string, or a FastAPI
 * validation array (`[{ loc, msg, type, ... }]`) as translated field messages
 * (see validationDetailText). Null when there is nothing usable. Lives here
 * rather than in `utils/notify` so callers that already hold a raw `detail` —
 * or that read it off a response-shaped throw — can normalize it without
 * importing the notification layer.
 */
export function formatApiErrorDetail(detail: unknown): string | null {
  if (typeof detail === 'string' && detail.length > 0) {
    return detail;
  }
  if (Array.isArray(detail)) {
    return validationDetailText(detail);
  }
  return null;
}

/**
 * The `detailCode` of a coded error response (`{ detail, detailCode,
 * detailParams }`), for callers that branch on which error it was; the
 * display text is translated, so never match on it.
 */
export function getApiErrorCode(error: unknown): string | undefined {
  const code = getApiErrorBody(error)?.detailCode;
  return typeof code === 'string' && code.length > 0 ? code : undefined;
}

/**
 * Whether the error is a 422 request-validation response with an entry for
 * the body field `field` (FastAPI's `[{ loc: ['body', field], msg }]`).
 */
export function isApiValidationErrorForField(
  error: unknown,
  field: string
): boolean {
  const detail = getRawApiErrorDetail(error);
  return (
    getApiErrorStatus(error) === 422 &&
    Array.isArray(detail) &&
    detail.some(
      (entry: unknown) =>
        isRecord(entry) && Array.isArray(entry.loc) && entry.loc.includes(field)
    )
  );
}

/**
 * The `name` of a thrown value, read from any error-like object. Media APIs can
 * reject with objects that are not `Error` instances (older engines'
 * OverconstrainedError), and callers branch on their name, so an
 * `instanceof Error` check would lose it.
 */
export function getErrorName(error: unknown): string | undefined {
  if (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    typeof error.name === 'string'
  ) {
    return error.name;
  }
  return undefined;
}

/** The `message` of a thrown value, read from any error-like object. */
export function getErrorMessage(error: unknown): string | undefined {
  if (typeof error === 'string') {
    return error;
  }
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }
  return undefined;
}

// Browsers reject `fetch` with a TypeError whose message names the failure
// ("Failed to fetch", "NetworkError when attempting to fetch resource.",
// "Load failed").
const FETCH_NETWORK_FAILURE = /failed to fetch|networkerror|load failed/i;

/** Whether `err` is a browser `fetch` network failure, not a server response. */
export function isFetchNetworkError(err: unknown): boolean {
  return err instanceof TypeError && FETCH_NETWORK_FAILURE.test(err.message);
}

const networkErrorText = (): string =>
  appText(
    'feedback.networkError',
    'Network error. Check your connection and try again.'
  );

/**
 * The message to show for a failure, always in the display language: the
 * text of getApiErrorText when present, a network error for a failed
 * `fetch`, else the caller's fallback. Any other error's own `message`
 * (English: axios's "Request failed with status code 500", a thrown
 * `new Error('...')`) is never shown; it stays on the error for logs. With an
 * empty fallback, a generic (or, for an axios network failure, a network)
 * error shows instead.
 */
export function extractErrorMessage(err: unknown, fallback: string): string {
  const detail = getApiErrorText(err);
  if (detail) {
    return detail;
  }
  if (isFetchNetworkError(err)) {
    return networkErrorText();
  }
  if (fallback) {
    return fallback;
  }
  if (isAxiosError(err) && !err.response && err.code === 'ERR_NETWORK') {
    return networkErrorText();
  }
  return appText(
    'feedback.genericError',
    'Something went wrong. Please try again.'
  );
}
